import React, { Suspense } from "react";

/**
 * LazyMapView — defers the heavy Mapbox bundle (mapbox-gl is ~250-400KB
 * gzipped) until the user actually switches to Map view. React.lazy +
 * dynamic import lets Webpack/Craco emit a separate chunk for MapView
 * and its mapbox-gl dependency; the chunk is fetched only when this
 * component is mounted by the parent search page.
 *
 * Consumers always import THIS file, not MapView.jsx directly, so the
 * lazy boundary is the single place we own.
 *
 * Props are passed straight through to the underlying MapView so callers
 * stay simple. Suspense fallback is a low-key skeleton tuned for the
 * editorial GMH register — no spinning loaders.
 */
const MapView = React.lazy(() => import("./MapView"));

const MapSkeleton = ({ height }) => (
  <div
    style={{ height }}
    className="relative w-full overflow-hidden border border-border bg-card"
    data-testid="map-loading-skeleton"
  >
    {/* Subtle radial vignette to suggest "map about to appear" without
        committing to a particular look. */}
    <div
      aria-hidden="true"
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(ellipse at 50% 50%, hsl(var(--muted)) 0%, hsl(var(--card)) 70%)",
      }}
    />
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="text-[12px] uppercase tracking-[0.18em] text-muted-foreground">
        Loading map…
      </div>
    </div>
  </div>
);

const LazyMapView = (props) => (
  <Suspense fallback={<MapSkeleton height={props.height || 640} />}>
    <MapView {...props} />
  </Suspense>
);

export default LazyMapView;
